import React, { useState } from "react";
import { Box, Flex, Switch, Text } from "@chakra-ui/react";
import AppButton from "@/features/line-sheet-sets/app-button";
import CustomApprovalModal from "@/shared/components/custom-modal";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import {
  PathPickMarket,
  PathPickProductCardComparisonLayout,
  PathPickProductCardViewSettings,
} from "@/features/pathpick/types";
import {
  PATH_PICK_MARKETS,
  PATH_PICK_MARKET_LABEL,
} from "@/features/pathpick/utils/market";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  value: PathPickProductCardViewSettings;
  onApply: (next: PathPickProductCardViewSettings) => void;
}

// 미리보기 카드 1장 — 실제 카드를 축소하지 않고 배치만 흉내낸다.
// 진짜 값을 채우면 "이 상품의 시세"로 읽혀서, 설정 화면에서 보여줄 것이 아닌 것까지 말하게 된다.
const PreviewMarketCard = ({ market }: { market: PathPickMarket }) => {
  const { t } = useI18nHelper();

  return (
    <Flex
      flexDirection={"column"}
      gap={"6px"}
      flex={1}
      minWidth={0}
      padding={"12px"}
      border={"0.5px solid #D4D4D1"}
      borderRadius={"8px"}
      background={"#FFFFFF"}
    >
      <Text textStyle={"body-sm-bold"} color={"#1D1D1B"}>
        {PATH_PICK_MARKET_LABEL[market]}
      </Text>
      <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
        {t("path_pick:card_view_settings.preview_basis_price")}
      </Text>
      <Box height={"6px"} width={"80%"} borderRadius={"3px"} background={"#DCEAFB"} />
      <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
        {t("path_pick:search_products.product_list.expected_margin")}
      </Text>
    </Flex>
  );
};

/**
 * 상품 카드 보기 설정 — KREAM·POIZON 비교 표시 여부와 PC 배치를 고른다.
 *
 * 드래프트 state를 들고 `설정 적용`에서만 저장한다. 카드 배치는 목록 전체가 다시 그려지는
 * 변경이라, 토글을 누르는 즉시 반영하면 뒤에 깔린 목록이 흔들려 비교하며 고를 수 없다.
 * 그래서 미리보기를 두고 적용 시점을 사용자가 잡게 한다.
 */
function PathPickProductCardViewSettingsModal({
  isOpen,
  onClose,
  value,
  onApply,
}: Props) {
  const { t } = useI18nHelper();
  const [draft, setDraft] = useState<PathPickProductCardViewSettings>(value);

  const layoutOptions: {
    value: PathPickProductCardComparisonLayout;
    label: string;
  }[] = [
    {
      value: "side",
      label: t("path_pick:card_view_settings.layout_side"),
    },
    {
      value: "stack",
      label: t("path_pick:card_view_settings.layout_stack"),
    },
  ];

  return (
    <CustomApprovalModal
      isOpen={isOpen}
      onClose={onClose}
      minWidth={"min(520px, 92dvw)"}
      isCentered={true}
    >
      <CustomApprovalModal.Header
        onClose={onClose}
        title={t("path_pick:card_view_settings.title")}
      />
      <CustomApprovalModal.Body>
        <Flex flexDirection={"column"} gap={"16px"}>
          <Text textStyle={"body-sm-medium"} color={"#8F8F8C"}>
            {t("path_pick:card_view_settings.description")}
          </Text>

          <Flex
            justifyContent={"space-between"}
            alignItems={"center"}
            gap={"12px"}
            padding={"12px 16px"}
            border={"0.5px solid #D4D4D1"}
            borderRadius={"8px"}
            background={"#F7F9FA"}
          >
            <Text textStyle={"body-sm-bold"} color={"#1D1D1B"}>
              {t("path_pick:card_view_settings.show_comparison")}
            </Text>
            <Switch
              colorScheme={"blue"}
              isChecked={draft.showComparison}
              onChange={(event) =>
                setDraft((prev) => ({
                  ...prev,
                  showComparison: event.target.checked,
                }))
              }
            />
          </Flex>

          <Flex flexDirection={"column"} gap={"8px"}>
            <Text textStyle={"body-sm-bold"} color={"#1D1D1B"}>
              {t("path_pick:card_view_settings.layout_section")}
            </Text>
            {/* 비교 표시를 끄면 배치는 의미가 없다 — 고른 값은 남겨 두고 선택만 잠근다
                (다시 켜면 이전 배치로 돌아온다). */}
            <Flex gap={"8px"}>
              {layoutOptions.map((option) => (
                <AppButton
                  key={option.value}
                  variant={"white"}
                  size={"medium"}
                  isDisabled={!draft.showComparison}
                  isActive={
                    draft.showComparison &&
                    draft.comparisonLayout === option.value
                  }
                  onClick={() =>
                    setDraft((prev) => ({
                      ...prev,
                      comparisonLayout: option.value,
                    }))
                  }
                  chakraProps={{ flex: 1 }}
                >
                  <Text textStyle={"body-sm-bold"}>{option.label}</Text>
                </AppButton>
              ))}
            </Flex>
          </Flex>

          <Flex flexDirection={"column"} gap={"8px"}>
            <Text textStyle={"body-sm-medium"} color={"#8F8F8C"}>
              {t("path_pick:card_view_settings.preview_label")}
            </Text>
            {draft.showComparison ? (
              <Flex
                flexDirection={
                  draft.comparisonLayout === "side" ? "row" : "column"
                }
                gap={"8px"}
                padding={"12px"}
                border={"0.5px solid #EFEFED"}
                borderRadius={"8px"}
                background={"#F7F9FA"}
              >
                {PATH_PICK_MARKETS.map((market) => (
                  <PreviewMarketCard key={market} market={market} />
                ))}
              </Flex>
            ) : (
              <Flex
                alignItems={"center"}
                justifyContent={"center"}
                minHeight={"96px"}
                padding={"12px"}
                border={"1px dashed #D4D4D1"}
                borderRadius={"8px"}
                background={"#F7F9FA"}
              >
                <Text textStyle={"body-sm-medium"} color={"#8F8F8C"}>
                  {t("path_pick:card_view_settings.preview_hidden")}
                </Text>
              </Flex>
            )}
          </Flex>
        </Flex>
      </CustomApprovalModal.Body>
      <CustomApprovalModal.Footer
        onCancel={onClose}
        onConfirm={() => onApply(draft)}
        confirmName={t("path_pick:card_view_settings.apply")}
      />
    </CustomApprovalModal>
  );
}

export default PathPickProductCardViewSettingsModal;
